/**
 * Account deletion: ONE routine that removes a player and every piece of data the backend keeps of it, behind both the
 * player's own `account.delete` (protocol v1, services/../backend.ts) and the operator's `players.delete` (console,
 * POST /admin, `npm run admin -- delete-player`).
 *
 * What goes, per storage collection (deps.ts C):
 *  - accounts, profiles (the canonical game state with its server state, pending battle and command journal),
 *    auth_sessions, auth_tokens, tokens (protocol 1), devices (guest device tokens), usernames (the login / nickname
 *    index), display_names (names the account chose), grants (pending), replays the player owns (they hold its private
 *    pre-battle game state and are listed under its id only), idempotency (the stored replies of its requests, found by
 *    their keyed owner tag), battles in which nobody else took part;
 *  - zones: a zone the player holds is released as the operator's reset does (no owner, stage 0), its attacks leave
 *    the zone history; auction: a lot it holds goes back to nobody at its current price, its permanent copies leave
 *    the winners list;
 *  - clans (services/clans.ts deletionOps): its membership record (clan_players) and its pending requests; its clan
 *    loses a member ('member_deleted' in the history), a leader is replaced by the officer who joined first (else the
 *    member who did; 'leader_auto'), and a clan it was the only member of is disbanded (clan, name, history,
 *    requests);
 *  - live state: its matchmaking tickets are cancelled, its battles forfeited (SessionService.removePlayer), its rooms
 *    left (a room left empty closes, a host's room gets another host), its connections closed, its mailboxes and event
 *    log dropped, its leaderboard entries and in-memory caches forgotten.
 * What is anonymized instead: records kept for OTHER players, i.e. an online battle (C.battles, a live session), the
 * replay another participant owns and the clan histories: the player's id becomes DELETED_PLAYER_ID ('p_deleted') and
 * its name DELETED_PLAYER_NAME, so the others keep their history without a reference to the deleted account (a replay
 * of the deleted player that such a battle points to is deleted, and the battle's `replayId` becomes null). The
 * history of the player's own clan changes in the batch; the histories of other clans that mention it (it was a member
 * once, it took their zones) right after it, each under its clan lock.
 * What stays: default names handed out to guests (C.defaultNames: they name nobody and are never issued again), news
 * (operators' texts), the operator audit (accountability: it refers to the player by id, and a rename by an operator
 * records neither name; only the operator's own free text, a reason or a search, keeps a name the operator wrote
 * there: admin/audit.ts), backups and log files (point-in-time copies the operator rotates).
 *
 * Concurrency and idempotency. The deletion ends every device session first and, until it is over, lets no new one
 * open (AuthService.beginDeletion: a sign-in, a guest's device token, a protocol 1 token get no session), so no
 * further request of the player passes the router. Then it takes the player out of the live services, then deletes the
 * stored data in ONE atomic storage batch while it holds, in this order, the player's profile lock (commands, saves,
 * loads, renames), every zone lock, the auction lock, the player's clan locks (clanp:<player>, and clan:<its clan>) and
 * the player's auth lock (sign-ins, refreshes, account writes): the lock order the services already use (profile
 * before zone, auction and the clans; auth last; services/clans.ts documents it), so it cannot deadlock with them. The
 * player's clan is read before the locks and checked under them: when it changed meanwhile (a request accepted, a
 * kick) the locks are taken again with the new clan. Writers of the account check under those locks that it still
 * exists (AuthService.saveAccount, openSession, link; ProfileService.withRecord), so nothing brings a part of it back.
 * Requests of the player that the router let through before the sessions ended may still create a room, a solo
 * session or a matchmaking ticket until the batch: the steps after the batch take the player out of the live
 * services again, and a battle that ends with a participant whose account is gone stores it anonymized
 * (SessionService.finalize).
 *
 * The batch also stores the deletion's record (C.accountDeletions: the report so far and the deleted replays' ids, no
 * name). The steps after the batch (the live services again, late replays, the other clans' histories, leaderboards,
 * caches) are repeatable and take no lock of the order above except one clan lock at a time (a clan history); the
 * record goes once they are all done. A deletion stopped in them (a storage failure, a crash) is finished by the next
 * call for that player, by the sweep (BackendCore.tick, every 10 minutes) and by the next start of the server
 * (`finishPending`), and a second deletion call that ran along with the one that stopped finishes it too (it reports
 * the deletion instead of null). A deletion of a player with neither an account nor such a record does nothing (null).
 */
import { isPlainObject } from '../../../shared/json';
import { DELETED_PLAYER_ID, DELETED_PLAYER_NAME, type AssaultZone, type PlayerId, type ReplayRecord } from '../../../shared/protocol/model';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import type { AssaultService } from './assault';
import { AUCTION_LOCK, type AuctionRecord, type AuctionService } from './auction';
import { type AuthService, authLockKey } from './auth';
import type { ClanDeletion, ClanService } from './clans';
import { C, type ServiceDeps } from './deps';
import type { LeaderboardService } from './leaderboard';
import { type GrantService, profileLockKey } from './profile';
import type { ReplayService } from './replays';
import type { MatchmakingService, RoomService } from './rooms';
import type { BattleDoc, SessionService } from './sessions';

/** Who deleted the account (logged). */
export type AccountDeletionActor = 'self' | 'operator';

/** What a deletion did (the operator console shows it; nothing in it names the player besides its id). */
export interface AccountDeletionReport {
  playerId: PlayerId;
  deletedAt: number;
  by: AccountDeletionActor;
  /** Documents deleted, by collection. */
  deleted: Record<string, number>;
  /** Documents kept for other players with the player anonymized, by collection. */
  anonymized: Record<string, number>;
  /** Device sessions ended. */
  sessionsEnded: number;
  /** Battle sessions this process ran that the player left (forfeited or anonymized). */
  battlesLeft: number;
  /** Rooms the player left. */
  roomsLeft: number;
  /** Assault zones released. */
  zonesReleased: number;
  /** The auction changed (a held lot released or won copies removed). */
  auctionChanged: boolean;
  /** The player's clan (null = none) and whether it was disbanded (the player was its only member). */
  clanId: number | null;
  clanDisbanded: boolean;
}

/**
 * The stored record of a deletion whose batch is stored and whose later steps are not all done yet (C.accountDeletions,
 * id = playerId). It names the player by id only.
 */
export interface AccountDeletionRecord {
  /** The report as of the batch (the later steps add to it). */
  report: AccountDeletionReport;
  /** The player's replays the batch deleted (ended battles this process keeps in memory may still point to them). */
  replayIds: string[];
}

function isDeletionRecord(v: unknown): v is AccountDeletionRecord {
  return isPlainObject(v) && isPlainObject(v.report) && typeof v.report.playerId === 'string' && Array.isArray(v.replayIds);
}

/** Hooks of the backend core (the idempotency store and the connections live there). */
export interface AccountDeletionHooks {
  /**
   * The ids of the stored idempotency replies of the player (under the deletion's locks), once the replies being written
   * are stored.
   */
  storedReplies(playerId: PlayerId): Promise<string[]>;
  /** After the batch: the replies are gone from storage (the core forgets them). */
  repliesDeleted(ids: readonly string[]): void;
  /** After everything: close the player's remaining connections, drop its mailboxes, event log and cached replies. */
  forgetPlayer(playerId: PlayerId): void;
}

export interface AccountDeletionServices {
  auth: AuthService;
  grants: GrantService;
  leaderboard: LeaderboardService;
  assault: AssaultService;
  auction: AuctionService;
  replays: ReplayService;
  sessions: SessionService;
  rooms: RoomService;
  matchmaking: MatchmakingService;
  clans: ClanService;
  /**
   * Other services with documents of the player that go with the account (store records, ad tickets, the Play Games
   * link, the account audit, the resource ledger; purchases stay as financial records): their deletionOps join the
   * batch.
   */
  extra?: readonly { deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> }[];
}

/** A copy of a JSON value in which every string equal to `playerId` is DELETED_PLAYER_ID. */
export function withoutPlayerId<T>(value: T, playerId: PlayerId): T {
  const walk = (v: unknown): unknown => {
    if (v === playerId) return DELETED_PLAYER_ID;
    if (Array.isArray(v)) return v.map(walk);
    if (isPlainObject(v)) {
      const out: Record<string, unknown> = {};
      for (const [k, x] of Object.entries(v)) out[k] = walk(x);
      return out;
    }
    return v;
  };
  return walk(value) as T;
}

/** Collections whose document id is the id of the player it belongs to (deps.ts C). */
const PLAYER_KEYED: ReadonlySet<string> = new Set([
  C.accounts,
  C.profiles,
  C.authSessions,
  C.clanPlayers,
  C.chatReads,
  C.chatMutes,
  C.storePlayers,
  C.adPlayers,
  C.playGamesPlayers,
  C.emailAccounts,
  C.integrity,
  C.anticheatPlayers,
]);

/**
 * The player a stored document belongs to (null: none, or the deleted player's placeholder DELETED_PLAYER_ID): its
 * `playerId` field when `value` is known, else what its id says in the collections keyed by player (the id itself,
 * the player part of `<clanId>~<playerId>` in clan_requests and of `<playerId>~<rule>` in anticheat_marks). A damaged
 * document has no value: only its id tells.
 */
export function documentOwner(collection: string, id: string, value?: unknown): PlayerId | null {
  let owner: string | null = null;
  if (isPlainObject(value) && typeof value.playerId === 'string') owner = value.playerId;
  else if (PLAYER_KEYED.has(collection)) owner = id;
  else if (collection === C.clanRequests) owner = id.slice(id.indexOf('~') + 1);
  else if (collection === C.anticheatMarks) owner = id.slice(0, Math.max(0, id.lastIndexOf('~')));
  return owner && owner !== DELETED_PLAYER_ID ? owner : null;
}

/** Whether a JSON value holds the string `playerId` anywhere. */
function mentions(value: unknown, playerId: PlayerId): boolean {
  return JSON.stringify(value ?? null).includes(JSON.stringify(playerId));
}

export class AccountDeletionService {
  /** Players whose `delete` runs now -> how many calls (finishPending leaves their records to them). */
  private readonly running = new Map<PlayerId, number>();
  /** The steps after the batch that run now, by player (a second caller gets the same run). */
  private readonly finishing = new Map<PlayerId, Promise<AccountDeletionReport>>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly s: AccountDeletionServices,
    private readonly hooks: AccountDeletionHooks,
  ) {}

  /**
   * Deletes the player (see the module comment). Returns what it did, or null when there is no such account (never
   * was, or already deleted: a repeated deletion changes nothing). A deletion of the player that stopped after its
   * batch is finished instead (its report).
   */
  async delete(playerId: PlayerId, by: AccountDeletionActor): Promise<AccountDeletionReport | null> {
    const { auth, sessions, rooms, matchmaking } = this.s;
    if (!(await auth.getAccount(playerId))) return this.finishLeft(playerId);
    this.running.set(playerId, (this.running.get(playerId) ?? 0) + 1);
    try {
      // 1. No request of the player passes the router from now on: every device session ends, its connections close,
      // and no new one opens until the deletion is over (a sign-in while it waits for its locks is refused).
      const endedFirst = await auth.beginDeletion(playerId);
      // 2. The live services let the player go (battles are forfeited and anonymized before anything is read below).
      matchmaking.cancelFor(playerId);
      const battlesLeft = (await sessions.removePlayer(playerId)).length;
      const roomsLeft = (await rooms.removePlayer(playerId)).length;
      // 3. The stored data and the deletion's record, in one batch under the locks of everything that could write it
      // meanwhile. The player's clan is read first and checked under the locks: when it changed in between, the locks
      // are taken again.
      const before = { by, sessionsEnded: endedFirst.length, battlesLeft, roomsLeft };
      const underLocks = async () => {
        const clan = await this.s.clans.deletionLocks(playerId);
        const locks = [profileLockKey(playerId), ...this.s.assault.zoneLockKeys(), AUCTION_LOCK, ...clan.keys, authLockKey(playerId)];
        return this.d.mutex.runAll(locks, () => this.deleteStored(playerId, clan.clanId, before));
      };
      let stored = await underLocks();
      for (let attempt = 1; stored === 'retry'; attempt++) {
        if (attempt >= 100) throw new Error(`account deletion of ${playerId}: the player's clan kept changing`);
        stored = await underLocks();
      }
      // Deleted by another call meanwhile: when that one stopped after its batch (or its later steps still run), this
      // one finishes it (or waits for them) and reports it, too.
      if (!stored) return await this.finishLeft(playerId);
      // 4. At once after the batch: the rest of the player's sessions end, the announcements to other players.
      auth.forgetDeleted(playerId, stored.auth);
      this.hooks.repliesDeleted(stored.replies);
      for (const zone of stored.released) this.d.hub.broadcast('assault.zoneChanged', { zone });
      if (stored.auction) await this.s.auction.announceRecord(stored.auction);
      await this.s.clans.deletionEvents(stored.clan);
      // 5. The steps of the record (repeatable: a deletion stopped in them is finished later).
      return await this.finish(stored.record, false);
    } finally {
      auth.endDeletion(playerId);
      const n = (this.running.get(playerId) ?? 0) - 1;
      if (n > 0) this.running.set(playerId, n);
      else this.running.delete(playerId);
    }
  }

  /** The player has no account: a deletion of it whose record is left is finished (its report), else null. */
  private async finishLeft(playerId: PlayerId): Promise<AccountDeletionReport | null> {
    const record = await this.d.storage.get<unknown>(C.accountDeletions, playerId);
    return isDeletionRecord(record) ? this.finish(record, true) : null;
  }

  /**
   * Finishes every deletion whose record is left (stopped after its batch by a storage failure or a crash), except the
   * ones whose `delete` runs now: the start of the server and the sweep call it. One that fails again stays for the
   * next time (logged). Returns the number finished.
   */
  async finishPending(): Promise<number> {
    let done = 0;
    for (const record of await this.d.storage.list<unknown>(C.accountDeletions)) {
      if (!isDeletionRecord(record)) {
        this.d.logger.error('account deletion record unreadable: skipped', {});
        continue;
      }
      if (this.running.has(record.report.playerId)) continue;
      try {
        await this.finish(record, true);
        done++;
      } catch {
        // Logged by finishSteps; the record stays for the next time.
      }
    }
    return done;
  }

  /** The steps after the batch that `record` stands for, one run per player at a time (see finishSteps). */
  private finish(record: AccountDeletionRecord, resumed: boolean): Promise<AccountDeletionReport> {
    const playerId = record.report.playerId;
    const current = this.finishing.get(playerId);
    if (current) return current;
    const run = this.finishSteps(record, resumed).finally(() => this.finishing.delete(playerId));
    this.finishing.set(playerId, run);
    return run;
  }

  /**
   * The steps after the batch, then the record goes. Each one is repeatable (once done, it does nothing the second
   * time), so they all run again when a deletion stopped in them (`resumed`: the next call, the sweep, a restart).
   */
  private async finishSteps(record: AccountDeletionRecord, resumed: boolean): Promise<AccountDeletionReport> {
    const { sessions, rooms, matchmaking, replays } = this.s;
    const playerId = record.report.playerId;
    const report: AccountDeletionReport = { ...record.report, deleted: { ...record.report.deleted }, anonymized: { ...record.report.anonymized } };
    try {
      // Requests of the player that the router let through before its sessions ended may have put it back into the
      // live services (a room, a solo session, a matchmaking ticket) after step 2: it leaves them again.
      matchmaking.cancelFor(playerId);
      report.battlesLeft += (await sessions.removePlayer(playerId)).length;
      report.roomsLeft += (await rooms.removePlayer(playerId)).length;
      await replays.forget(playerId, record.replayIds);
      // A replay stored by a request that was already under way when the sessions ended (replay.save) goes too.
      const late = await replays.idsOf(playerId);
      if (late.length > 0) {
        await this.d.storage.batch(late.map((id) => deleteOp(C.replays, id)));
        await replays.forget(playerId, late);
        report.deleted[C.replays] = (report.deleted[C.replays] ?? 0) + late.length;
      }
      // Ended battles this process still keeps in memory point to none of the deleted replays (as the stored ones).
      sessions.forgetReplays(new Set([...record.replayIds, ...late]));
      // The player's id leaves the histories of the other clans.
      const histories = await this.s.clans.deletionHistories(playerId);
      if (histories > 0) report.anonymized[C.clanHistory] = (report.anonymized[C.clanHistory] ?? 0) + histories;
      await this.s.leaderboard.remove(playerId);
      this.hooks.forgetPlayer(playerId);
      await this.d.storage.delete(C.accountDeletions, playerId);
    } catch (err) {
      this.d.logger.error('account deletion stopped after its batch: the next call, the sweep or the next start finishes it', { playerId, err });
      throw err;
    }
    const facts = { playerId, by: report.by, deleted: report.deleted, anonymized: report.anonymized, sessionsEnded: report.sessionsEnded };
    this.d.logger.warn(resumed ? 'account deletion finished' : 'account deleted', facts);
    return report;
  }

  /**
   * Step 3 of `delete`, under the locks: collects every write and stores them in one batch with the deletion's record.
   * 'retry': the player's clan is no longer `clanId` (the locks it holds are not the right ones); null: no such account
   * any more.
   */
  private async deleteStored(playerId: PlayerId, clanId: number | null, before: Pick<AccountDeletionReport, 'by' | 'sessionsEnded' | 'battlesLeft' | 'roomsLeft'>) {
    const { auth, grants, assault, auction, replays, clans } = this.s;
    if (!(await auth.getAccount(playerId))) return null;
    await this.dropDamaged(playerId);
    const at = this.d.clock.now();
    const clan: ClanDeletion | null = await clans.deletionOps(playerId, clanId, at);
    if (!clan) return 'retry' as const;
    const deleted: Record<string, number> = {};
    const anonymized: Record<string, number> = {};
    const count = (into: Record<string, number>, collection: string, n = 1) => {
      if (n > 0) into[collection] = (into[collection] ?? 0) + n;
    };
    const authPart = await auth.deletionWrites(playerId);
    for (const [c, n] of Object.entries(authPart.counts)) count(deleted, c, n);
    const ops: StorageBatchOp[] = [...authPart.ops];
    if (await this.d.storage.get(C.profiles, playerId)) {
      ops.push(deleteOp(C.profiles, playerId));
      count(deleted, C.profiles);
    }
    const grantOps = await grants.deletionOps(playerId);
    ops.push(...grantOps);
    count(deleted, C.grants, grantOps.length);
    const zones = await assault.deletionOps(playerId);
    ops.push(...zones.ops);
    count(anonymized, C.zones, zones.ops.length);
    const lot = await auction.deletionOps(playerId, at);
    ops.push(...lot.ops);
    count(anonymized, C.auction, lot.ops.length);
    const replayIds = await replays.idsOf(playerId);
    ops.push(...replayIds.map((id) => deleteOp(C.replays, id)));
    count(deleted, C.replays, replayIds.length);
    const battles = await this.battleWrites(playerId, new Set(replayIds));
    ops.push(...battles.ops);
    count(deleted, C.battles, battles.deleted);
    count(anonymized, C.battles, battles.anonymized);
    count(anonymized, C.replays, battles.replaysAnonymized);
    const replies = await this.hooks.storedReplies(playerId);
    ops.push(...replies.map((id) => deleteOp(C.idempotency, id)));
    count(deleted, C.idempotency, replies.length);
    for (const service of this.s.extra ?? []) {
      const more = await service.deletionOps(playerId);
      ops.push(...more);
      for (const o of more) if (o.op === 'delete') count(deleted, o.collection);
    }
    ops.push(...clan.ops);
    for (const o of clan.ops) {
      if (o.op === 'delete') count(deleted, o.collection);
      else if (o.collection === C.clanHistory) count(anonymized, o.collection);
    }
    const record: AccountDeletionRecord = {
      report: {
        playerId,
        deletedAt: at,
        by: before.by,
        deleted,
        anonymized,
        sessionsEnded: before.sessionsEnded + authPart.sessionIds.length,
        battlesLeft: before.battlesLeft,
        roomsLeft: before.roomsLeft,
        zonesReleased: zones.released.length,
        auctionChanged: lot.record !== null,
        clanId: clan.clanId,
        clanDisbanded: clan.disbanded,
      },
      replayIds,
    };
    ops.push(putOp(C.accountDeletions, playerId, record));
    await this.d.storage.batch(ops);
    clans.deletionStored(clan);
    await grants.forget(playerId);
    for (const doc of battles.stored) await this.s.sessions.archive.stored(doc);
    for (const id of battles.removed) await this.s.sessions.archive.removed(id);
    const auctionRecord: AuctionRecord | null = lot.record;
    const released: AssaultZone[] = zones.released;
    return { auth: authPart, replies, released, auction: auctionRecord, clan, record };
  }

  /**
   * The player's documents the storage found damaged (unreadable, so no index has them and the batch could neither
   * read nor delete them) go with the account, and so do their copies in the quarantine: only the ones whose id names
   * the player (documentOwner) are known to be its. A damaged document of the player with another id (a chat message,
   * a replay) cannot be told apart: the operator's repair refuses to bring it back once the account is gone
   * (BackendCore.deletedOwner).
   */
  private async dropDamaged(playerId: PlayerId): Promise<void> {
    const storage = this.d.storage;
    if (!storage.health || !storage.repairDamaged) return;
    for (const d of storage.health().damaged) {
      if (documentOwner(d.collection, d.id) !== playerId) continue;
      await storage.repairDamaged(d.collection, d.id, null, { dropQuarantine: true });
      this.d.logger.warn('damaged document of a deleted account removed', { collection: d.collection, id: d.id, playerId });
    }
  }

  /**
   * The writes of the stored battles the player took part in: one of the player alone (or with deleted accounts only)
   * is deleted; one with other players is kept for them with the player anonymized, and so is the replay another
   * participant owns (its online init names the teams' players); a replay of the deleted player it points to is
   * deleted with the player's replays, so its `replayId` becomes null.
   */
  private async battleWrites(
    playerId: PlayerId,
    deletedReplays: ReadonlySet<string>,
  ): Promise<{ ops: StorageBatchOp[]; deleted: number; anonymized: number; replaysAnonymized: number; stored: BattleDoc[]; removed: string[] }> {
    const ops: StorageBatchOp[] = [];
    const stored: BattleDoc[] = [];
    const removed: string[] = [];
    let deleted = 0;
    let anonymized = 0;
    let replaysAnonymized = 0;
    // The player's battles come from the archive's index (services/battle-archive.ts): only these are read.
    for (const summary of await this.s.sessions.archive.ofPlayer(playerId)) {
      const doc = await this.d.storage.get<BattleDoc>(C.battles, summary.sessionId);
      const participants = doc?.info?.participants;
      if (!doc || typeof doc.sessionId !== 'string' || !Array.isArray(participants) || !mentions(doc.info, playerId)) continue;
      if (!participants.some((p) => p?.playerId !== playerId && p?.playerId !== DELETED_PLAYER_ID)) {
        ops.push(deleteOp(C.battles, doc.sessionId));
        removed.push(doc.sessionId);
        deleted++;
        continue;
      }
      const info = withoutPlayerId(doc.info, playerId);
      for (const p of info.participants) if (p.playerId === DELETED_PLAYER_ID) p.displayName = DELETED_PLAYER_NAME;
      const replayId = info.outcome?.replayId ?? null;
      if (replayId !== null && deletedReplays.has(replayId)) info.outcome!.replayId = null;
      else if (replayId !== null) {
        const replay = await this.d.storage.get<ReplayRecord>(C.replays, replayId);
        if (replay && replay.playerId !== playerId && mentions(replay.init, playerId)) {
          ops.push(putOp(C.replays, replayId, { ...replay, init: withoutPlayerId(replay.init, playerId) } satisfies ReplayRecord));
          replaysAnonymized++;
        }
      }
      const next: BattleDoc = { ...doc, info };
      ops.push(putOp(C.battles, doc.sessionId, next));
      stored.push(next);
      anonymized++;
    }
    return { ops, deleted, anonymized, replaysAnonymized, stored, removed };
  }
}
